import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SignedIn, SignedOut, SignInButton } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { redeemAuditClaim } from "@/lib/api";
import { useAcceptanceGate } from "@/components/legal/useAcceptanceGate";

/**
 * Where the Margin Lens Shopify app sends a merchant who bought the Margin
 * Audit there. The link is single-use: linking binds that purchase to the
 * signed-in account, which then owns the audit exactly as a website purchase
 * does, and the merchant continues to the usual intake form. The same checkout
 * documents are required as on the website; if any are outstanding the server
 * answers 428 and the usual consent step opens.
 */
export default function AuditClaimPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { guard, gateNode } = useAcceptanceGate();
  const [state, setState] = useState({ status: "idle", message: null });

  const link = async () => {
    setState({ status: "working", message: null });
    try {
      const out = await guard("checkout", async () => {
        const r = await redeemAuditClaim(token);
        navigate(`/account/audit-intake/${r.session_id}`);
        return r;
      }, {
        title: "Before we link your audit",
        lede: "The Margin Audit is provided under the Services Agreement. Please confirm you accept it.",
      });
      if (!out) setState({ status: "idle", message: null });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setState({ status: "error",
                 message: typeof detail === "string" ? detail : "This link could not be used. Please try again." });
    }
  };

  return (
    <>
      <Nav />
      <div className="pt-32 pb-24 max-w-[720px] mx-auto px-6 md:px-10">
        <div className="card-cream p-10" data-testid="audit-claim-panel">
          <div className="tick mb-6">Margin Audit from the Shopify app</div>
          <h1 className="font-serif-display text-4xl text-[#171717] mb-6 leading-tight">
            Link your audit to a Margin Lens account.
          </h1>
          <p className="font-mono-tech text-[13px] text-[#525252] leading-relaxed mb-8">
            Your audit was paid for in Shopify. It reads your own P&amp;L, so the next step is a
            short intake form here. The account you link it to is the one that receives the
            finished audit.
          </p>

          <SignedOut>
            <SignInButton mode="modal">
              <button className="btn-primary" data-testid="audit-claim-signin">Sign in or create an account</button>
            </SignInButton>
          </SignedOut>

          <SignedIn>
            <button className="btn-primary" onClick={link} disabled={state.status === "working"}
                    data-testid="audit-claim-link">
              {state.status === "working"
                ? <span className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Linking</span>
                : "Link this audit and continue"}
            </button>
          </SignedIn>

          {state.status === "error" && (
            <p className="font-mono-tech text-[13px] text-[#9a3412] mt-6" data-testid="audit-claim-error">
              {state.message}
            </p>
          )}
        </div>
      </div>
      <Footer />
      {gateNode}
    </>
  );
}
